import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  IconButton,
  Chip,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import BuildIcon from '@mui/icons-material/Build';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const categoryConfig = {
  feature: {
    icon: NewReleasesIcon,
    color: 'success',
    labelKey: 'whatsnew.newFeature',
  },
  improvement: {
    icon: BuildIcon,
    color: 'info',
    labelKey: 'whatsnew.improvement',
  },
  fix: {
    icon: BugReportIcon,
    color: 'warning',
    labelKey: 'whatsnew.bugFix',
  },
};

function WhatsNewDialog({ open, onClose, lastViewedAt }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchItems();
    }
  }, [open]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users/whatsnew/', { withCredentials: true });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching what\'s new items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    try {
      await axios.post('/api/users/whatsnew/mark-viewed/', {}, { withCredentials: true });
    } catch (error) {
      console.error('Error marking what\'s new as viewed:', error);
    }
    onClose();
  };

  const isUnread = (item) => {
    if (!lastViewedAt) return true;
    return new Date(item.published_at) > new Date(lastViewedAt);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fi-FI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 1, sm: 4 },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2, sm: 4 },
          fontSize: '1.125rem',
          fontWeight: 700,
        }}
      >
        {t('whatsnew.title')}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('whatsnew.noItems')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item, index) => {
              const config = categoryConfig[item.category] || categoryConfig.feature;
              const CategoryIcon = config.icon;
              const unread = isUnread(item);

              return (
                <ListItem
                  key={item.id}
                  divider={index < items.length - 1}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2.5, sm: 3.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, width: '100%' }}>
                    <Chip
                      icon={<CategoryIcon />}
                      label={t(config.labelKey)}
                      color={config.color}
                      size="small"
                    />
                    {unread && (
                      <Chip
                        label={t('whatsnew.new')}
                        color="primary"
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: unread ? 1 : 'auto' }}>
                      {formatDate(item.published_at)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.2rem', sm: '1.375rem' },
                      lineHeight: 1.35,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                      mb: 2,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      color: 'text.primary',
                      fontSize: { xs: '0.9rem', sm: '0.9375rem' },
                      lineHeight: 1.65,
                      '& p': { m: 0, mb: 2 },
                      '& p:last-child': { mb: 0 },
                      '& strong': { fontWeight: 700 },
                      '& h3': { fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.4, mt: 3, mb: 1 },
                      '& h4': { fontSize: '1rem', fontWeight: 700, lineHeight: 1.4, mt: 2, mb: 0.5 },
                      '& h3:first-child, & h4:first-child': { mt: 0 },
                      '& ul, & ol': { m: 0, pl: 3, mb: 2 },
                      '& ul': { listStyleType: 'disc' },
                      '& li': { mb: 1.5, pl: 0.5 },
                      '& li::marker': { color: 'primary.main' },
                      '& li > p': { mb: 0 },
                      '& li:last-child': { mb: 0 },
                      '& code': {
                        bgcolor: 'action.hover',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontSize: '0.8em',
                      },
                      '& pre': {
                        bgcolor: 'action.hover',
                        p: 1,
                        borderRadius: 1,
                        overflow: 'auto',
                      },
                      '& a': { color: 'primary.main' },
                    }}
                  >
                    <ReactMarkdown>{item.content}</ReactMarkdown>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default WhatsNewDialog;
